/**
 * Nominatim (OpenStreetMap) autocomplete / geocoding adapter.
 *
 * Provides a `google.maps.places.PlaceResult`-shaped shim so the existing
 * `parsePlace()` extractor in `GooglePlacesPicker.tsx` keeps working unchanged.
 *
 * Public server rate policy is 1 req/sec — we already debounce 350 ms in the
 * UI, so we're well inside the budget. Failures throw / return an empty list;
 * callers surface a graceful "keep typing" fallback.
 */

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const NOMINATIM_REVERSE_URL = 'https://nominatim.openstreetmap.org/reverse';

/** A single Nominatim jsonv2 result. */
export interface NominatimHit {
  place_id: number;
  osm_type: string;
  osm_id: number;
  lat: string;
  lon: string;
  /** Bounding box as [south, north, west, east] strings. */
  boundingbox?: [string, string, string, string];
  display_name: string;
  /** jsonv2 name for what old jsonv1 called `class`. Both accepted; jsonv2 is
   *  what the public server actually returns today. */
  category?: string;
  class?: string;
  type?: string;
  address?: {
    house_number?: string;
    road?: string;
    pedestrian?: string;
    path?: string;
    suburb?: string;
    neighbourhood?: string;
    city?: string;
    town?: string;
    village?: string;
    county?: string;
    state_district?: string;
    state?: string;
    country?: string;
    country_code?: string;
    postcode?: string;
  };
}

/** Search for places matching the free-text `query`. */
export async function searchNominatim(query: string): Promise<NominatimHit[]> {
  const q = query.trim();
  if (!q) return [];
  const params = new URLSearchParams({
    format: 'jsonv2',
    addressdetails: '1',
    limit: '8',
    q,
  });
  const url = `${NOMINATIM_URL}?${params.toString()}`;
  try {
    const res = await fetch(url, {
      headers: { 'Accept-Language': 'en' },
    });
    if (!res.ok) return [];
    const json = (await res.json()) as unknown;
    return Array.isArray(json) ? (json as NominatimHit[]) : [];
  } catch {
    return [];
  }
}

/** Reverse-geocode a coordinate pair. Returns `null` on failure. */
export async function reverseNominatim(
  lat: number,
  lng: number,
): Promise<NominatimHit | null> {
  const params = new URLSearchParams({
    format: 'jsonv2',
    addressdetails: '1',
    lat: String(lat),
    lon: String(lng),
  });
  const url = `${NOMINATIM_REVERSE_URL}?${params.toString()}`;
  try {
    const res = await fetch(url, {
      headers: { 'Accept-Language': 'en' },
    });
    if (!res.ok) return null;
    const json = (await res.json()) as NominatimHit | null;
    return json ?? null;
  } catch {
    return null;
  }
}

/**
 * Build the `place.types[]` array that `parsePlace()` uses to decide between
 * the viewport-center and the point coordinate. Mirrors Google's Places-API
 * `types` semantics closely enough for the region-vs-address branch.
 */
/**
 * Google Places' `address_components.locality.long_name` for London gives
 * "Westminster" — but Nominatim returns "City of Westminster". `parsePlace`
 * then runs `trimCityWords` on the final city, which strips `\b(City|County|
 * Metro)\b` anywhere in the string, turning "City of Westminster" into
 * "of Westminster". Strip the leading "City of " prefix at the shim boundary
 * so parsePlace sees Google-shaped input. Same idea for "Borough of X"
 * (Greater London boroughs) where the borough name is what the user expects
 * to see.
 */
function normalizeCityName(name: string | undefined): string | undefined {
  if (!name) return name;
  return name.replace(/^\s*(City|Borough)\s+of\s+/i, '').trim() || name;
}

function typesFromHit(hit: NominatimHit): string[] {
  // Nominatim renamed `class` → `category` in jsonv2 (which is what we request).
  // Read both so we're forwards- and backwards-compatible. Without this the
  // Region-vs-address branch in parsePlace would never fire for jsonv2 hits
  // and every city pick would get `geometry.location` instead of the
  // viewport centre — reintroducing the "Patiala lat inside Chandigarh"
  // bug parsePlace's viewport-fallback was written to prevent.
  const cls = hit.category ?? hit.class ?? '';
  const type = hit.type ?? '';
  if (cls === 'place') {
    if (type === 'city' || type === 'town' || type === 'village') {
      return ['locality', 'political'];
    }
    if (type === 'suburb' || type === 'neighbourhood' || type === 'quarter') {
      return ['sublocality_level_1', 'sublocality', 'political'];
    }
    if (type === 'country') return ['country', 'political'];
    if (type === 'state' || type === 'province' || type === 'region') {
      return ['administrative_area_level_1', 'political'];
    }
    if (type === 'county') return ['administrative_area_level_2', 'political'];
    if (type === 'postcode') return ['postal_code'];
  }
  if (cls === 'boundary' && type === 'administrative') {
    // Fall back on which address fields are populated to guess the admin
    // level — states usually have state populated, districts have county.
    if (hit.address?.state && !hit.address.city && !hit.address.town) {
      return ['administrative_area_level_1', 'political'];
    }
    if (hit.address?.county && !hit.address.city && !hit.address.town) {
      return ['administrative_area_level_2', 'political'];
    }
    return ['locality', 'political'];
  }
  if (cls === 'highway') return ['route'];
  if (cls === 'building') return ['premise', 'street_address'];
  if (cls === 'amenity' || cls === 'shop' || cls === 'tourism') {
    return ['point_of_interest', 'establishment'];
  }
  return ['street_address'];
}

/**
 * Synthesize a `google.maps.places.PlaceResult` from a Nominatim hit so
 * `parsePlace()` — the byte-for-byte-preserved extractor — can consume it.
 *
 * Only the fields `parsePlace` actually reads (`address_components`,
 * `geometry.location`, `geometry.viewport`, `types`, `name`,
 * `formatted_address`) are populated. The lat/lng and viewport-center
 * accessors are function-valued to match Google's LatLng contract.
 */
export function toPlaceResult(hit: NominatimHit): google.maps.places.PlaceResult {
  const addr = hit.address ?? {};
  const components: google.maps.GeocoderAddressComponent[] = [];

  const push = (
    long: string | undefined,
    short: string | undefined,
    types: string[],
  ) => {
    if (!long) return;
    components.push({
      long_name: long,
      short_name: short ?? long,
      types,
    });
  };

  push(addr.house_number, addr.house_number, ['street_number']);
  const road = addr.road ?? addr.pedestrian ?? addr.path;
  push(road, road, ['route']);
  push(addr.suburb, addr.suburb, ['sublocality', 'sublocality_level_1']);
  push(addr.neighbourhood, addr.neighbourhood, ['neighborhood']);
  const cityLike = normalizeCityName(addr.city ?? addr.town ?? addr.village);
  push(cityLike, cityLike, ['locality', 'political']);
  push(addr.state_district, addr.state_district, [
    'administrative_area_level_3',
    'political',
  ]);
  push(addr.county, addr.county, ['administrative_area_level_2', 'political']);
  push(addr.state, addr.state, ['administrative_area_level_1', 'political']);
  if (addr.country) {
    components.push({
      long_name: addr.country,
      short_name: (addr.country_code ?? addr.country).toUpperCase(),
      types: ['country', 'political'],
    });
  }
  push(addr.postcode, addr.postcode, ['postal_code']);

  const lat = Number.parseFloat(hit.lat);
  const lng = Number.parseFloat(hit.lon);
  const location = {
    lat: () => lat,
    lng: () => lng,
  } as unknown as google.maps.LatLng;

  let viewport: google.maps.LatLngBounds | undefined;
  if (hit.boundingbox && hit.boundingbox.length === 4) {
    const [southStr, northStr, westStr, eastStr] = hit.boundingbox;
    const south = Number.parseFloat(southStr);
    const north = Number.parseFloat(northStr);
    const west = Number.parseFloat(westStr);
    const east = Number.parseFloat(eastStr);
    if (
      Number.isFinite(south) &&
      Number.isFinite(north) &&
      Number.isFinite(west) &&
      Number.isFinite(east)
    ) {
      const centerLat = (south + north) / 2;
      const centerLng = (west + east) / 2;
      viewport = {
        getCenter: () =>
          ({
            lat: () => centerLat,
            lng: () => centerLng,
          }) as unknown as google.maps.LatLng,
      } as unknown as google.maps.LatLngBounds;
    }
  }

  return {
    address_components: components,
    geometry: {
      location,
      viewport,
    } as google.maps.places.PlaceGeometry,
    formatted_address: hit.display_name,
    name: hit.display_name.split(',')[0]?.trim() ?? hit.display_name,
    place_id: String(hit.place_id),
    types: typesFromHit(hit),
  } as google.maps.places.PlaceResult;
}

/**
 * Drop case-insensitive duplicate segments while preserving order. Nominatim's
 * `display_name` is a naive comma-join of every address level, so buildings
 * that carry the same name at multiple hierarchy levels (or OSM entities with
 * both `name` and `alt_name` on parent boundaries) produce visible dupes like:
 *   - "Sector 73, Sector 74, Sector 73"  (exact repeat — pass 1)
 *   - "C106, phase 7, C106 Phase 7"      (short segments contained inside a
 *      longer neighbour — pass 2)
 *   - "Mohali, S.A.S. Nagar (Mohali) Tahsil"  (short segment appears as a
 *      whole word inside a longer neighbour — pass 2)
 */
function dedupeSegments(parts: string[]): string[] {
  // Pass 1: strict case-insensitive dedupe, preserving first-seen order.
  const seen = new Set<string>();
  const uniq: string[] = [];
  for (const p of parts) {
    const key = p.toLowerCase().replace(/\s+/g, ' ').trim();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    uniq.push(p);
  }
  // Pass 2: drop any segment whose text appears as a whole-word substring
  // inside a strictly longer segment. Only applied when the shorter segment
  // is ≤ 25 chars so we don't collapse legitimate distinct place names that
  // happen to share a prefix. The LAST segment is also protected — Nominatim
  // always ends display_name with the country, and short country names
  // (e.g. "France", "India") would otherwise get swallowed by longer
  // sibling segments that contain the same word ("Metropolitan France",
  // "Avenue Anatole France").
  const lastIdx = uniq.length - 1;
  return uniq.filter((seg, i) => {
    if (i === lastIdx) return true;
    if (seg.length > 25) return true;
    const escaped = seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    let re: RegExp;
    try {
      re = new RegExp(`\\b${escaped}\\b`, 'i');
    } catch {
      return true;
    }
    return !uniq.some(
      (other) => other !== seg && other.length > seg.length && re.test(other),
    );
  });
}

/** Break a `display_name` into "main / secondary" halves for the dropdown. */
export function splitDisplayName(displayName: string): {
  mainText: string;
  secondaryText: string;
} {
  const parts = displayName.split(',').map((s) => s.trim()).filter(Boolean);
  const deduped = dedupeSegments(parts);
  const mainText = deduped[0] ?? displayName;
  // Also drop any tail segment that equals the mainText (case-insensitive).
  const mainKey = mainText.toLowerCase();
  const secondaryText = deduped
    .slice(1)
    .filter((p) => p.toLowerCase() !== mainKey)
    .join(', ');
  return { mainText, secondaryText };
}

/**
 * Return the display_name with duplicate comma-segments collapsed, suitable
 * for use as the input value / fallback label when parsePlace comes up empty.
 * Capped at 6 segments so unusually deep addresses stay readable.
 */
export function cleanDisplayName(displayName: string, maxSegments = 6): string {
  const parts = displayName.split(',').map((s) => s.trim()).filter(Boolean);
  return dedupeSegments(parts).slice(0, maxSegments).join(', ');
}
